import { forwardRef, useImperativeHandle, useRef } from 'react';

interface AccordionSectionProps {
  stepNumber: number;
  title: string;
  summary?: string;
  isActive: boolean;
  isComplete: boolean;
  canActivate: boolean;
  onActivate: () => void;
  children: React.ReactNode;
}

export interface AccordionSectionHandle {
  scrollIntoView: () => void;
}

export const AccordionSection = forwardRef<AccordionSectionHandle, AccordionSectionProps>(
  function AccordionSection(
    { stepNumber, title, summary, isActive, isComplete, canActivate, onActivate, children },
    ref
  ) {
    const sectionRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => ({
      scrollIntoView: () => {
        sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      },
    }));

    const handleHeaderClick = () => {
      if (canActivate) {
        onActivate();
      }
    };

    return (
      <div ref={sectionRef} className="mb-3">
        <div className={`overflow-hidden rounded-xl border-2 transition-all duration-200 ${
          isActive
            ? 'border-green-500 bg-white shadow-sm'
            : isComplete
              ? 'border-gray-200 bg-white hover:border-gray-300'
              : 'border-gray-200 bg-gray-50'
        }`}>
          <button
            type="button"
            className={`flex w-full items-center gap-3 px-4 py-3.5 text-left ${
              canActivate ? 'cursor-pointer' : 'cursor-default'
            }`}
            onClick={handleHeaderClick}
            disabled={!canActivate}
          >
            <div
              className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-sm font-semibold ${
                isComplete || isActive
                  ? 'bg-green-500 text-white'
                  : 'bg-gray-200 text-gray-500'
              }`}
            >
              {isComplete && !isActive ? (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                  <polyline points="20 6 9 17 4 12" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              ) : (
                stepNumber
              )}
            </div>

            <span className={`flex-1 text-[15px] font-semibold ${isActive || isComplete ? 'text-gray-800' : 'text-gray-400'}`}>
              {title}
            </span>

            {!isActive && isComplete && summary && (
              <span className="max-w-[200px] truncate text-sm text-gray-500">{summary}</span>
            )}

            <svg
              width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
              className={`flex-shrink-0 text-gray-400 transition-transform duration-200 ${isActive ? 'rotate-180' : ''}`}
            >
              <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          {isActive && (
            <div className="border-t border-gray-200 px-4 py-4">
              {children}
            </div>
          )}
        </div>
      </div>
    );
  }
);
